import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Loader2, BookOpen, Download, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { adminFetch } from '../utils/adminAuth';

export default function AdminDocs() {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await adminFetch('/api/bloom-gestion-secure/docs');
        if (r.ok) {
          const d = await r.json();
          setContent(d.content || '');
        } else {
          toast.error('Documentation introuvable');
        }
      } catch (e) {
        toast.error('Erreur de chargement');
      } finally { setLoading(false); }
    };
    load();
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast.success('Documentation copiée dans le presse-papiers');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Impossible de copier');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'BLOOM_HANDOFF.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#D4AF37]" size={28} /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="admin-docs-page">
      {/* Header */}
      <div className="mb-6 pb-4 border-b border-[#E0DFDD]">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="text-[#D4AF37]" size={24} />
          <h1 className="font-serif text-2xl">Documentation Projet</h1>
        </div>
        <p className="text-sm text-[#6B6A68] mb-4">
          Vue d'ensemble complète de Bloom Lingerie : architecture, fonctionnalités, intégrations, roadmap. À partager avec un développeur ou une autre IA pour qu'ils aient le contexte complet du projet en un seul fichier.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleCopy}
            className="px-3 py-2 text-xs font-medium border border-[#1A1514] hover:bg-[#1A1514] hover:text-white inline-flex items-center gap-1.5 transition-colors"
            data-testid="copy-docs-btn"
          >
            {copied ? <><Check size={12} /> Copié</> : <><Copy size={12} /> Copier tout</>}
          </button>
          <button
            onClick={handleDownload}
            className="px-3 py-2 text-xs font-medium bg-[#D4AF37] text-[#1A1514] hover:bg-[#B8941F] inline-flex items-center gap-1.5 transition-colors"
            data-testid="download-docs-btn"
          >
            <Download size={12} /> Télécharger .md
          </button>
        </div>
      </div>

      {/* Markdown content */}
      <article className="bg-white border border-[#E0DFDD] p-6 md:p-10 prose-doc">
        <ReactMarkdown
          components={{
            h1: ({ node, ...props }) => <h1 className="font-serif text-3xl mt-8 mb-4 first:mt-0" {...props} />,
            h2: ({ node, ...props }) => <h2 className="font-serif text-xl mt-8 mb-3 pb-2 border-b border-[#E0DFDD] text-[#1A1514]" {...props} />,
            h3: ({ node, ...props }) => <h3 className="font-medium text-base mt-5 mb-2 text-[#1A1514]" {...props} />,
            p: ({ node, ...props }) => <p className="text-sm leading-relaxed mb-3 text-[#3A3633]" {...props} />,
            ul: ({ node, ...props }) => <ul className="list-disc list-outside ml-5 space-y-1 my-3 text-sm text-[#3A3633]" {...props} />,
            ol: ({ node, ...props }) => <ol className="list-decimal list-outside ml-5 space-y-1 my-3 text-sm text-[#3A3633]" {...props} />,
            li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
            code: ({ node, inline, ...props }) => inline
              ? <code className="bg-[#F5F4F2] text-[#1A1514] px-1.5 py-0.5 text-[12px] font-mono rounded" {...props} />
              : <code className="block bg-[#1A1514] text-[#D4AF37] p-3 my-3 text-[12px] font-mono leading-relaxed overflow-x-auto" {...props} />,
            pre: ({ node, ...props }) => <pre className="bg-[#1A1514] text-[#D4AF37] p-4 my-4 text-[12px] font-mono leading-relaxed overflow-x-auto rounded" {...props} />,
            a: ({ node, ...props }) => <a className="text-[#D4AF37] hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
            blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-[#D4AF37] bg-[#FFFBEB] pl-4 py-2 my-3 text-sm italic text-[#1A1514]" {...props} />,
            strong: ({ node, ...props }) => <strong className="font-semibold text-[#1A1514]" {...props} />,
            hr: ({ node, ...props }) => <hr className="my-8 border-[#E0DFDD]" {...props} />,
            table: ({ node, ...props }) => <div className="overflow-x-auto my-4"><table className="text-sm border-collapse w-full" {...props} /></div>,
            th: ({ node, ...props }) => <th className="border border-[#E0DFDD] bg-[#F5F4F2] px-3 py-2 text-left font-medium" {...props} />,
            td: ({ node, ...props }) => <td className="border border-[#E0DFDD] px-3 py-2" {...props} />,
          }}
        >
          {content}
        </ReactMarkdown>
      </article>

      <p className="text-xs text-[#9E9C9A] mt-4 text-center">
        Source : <code className="bg-[#F5F4F2] px-1.5 py-0.5 font-mono">/app/HANDOFF_FOR_CLAUDE.md</code>
      </p>
    </div>
  );
}
